import { useToast } from '../../contexts/ToastContext'
import { buildScanText, svgQrDataUrl, type QrPayload } from './orderQr'

export interface PrintLabelItem {
  name: string
  quantity: number
}

export interface PrintLabelOrder {
  orderNumber: string
  customerName: string
  items: PrintLabelItem[]
  // Distinct service name(s) in this order, e.g. ['Wash & Fold', 'Dry Cleaning'].
  serviceNames?: string[]
  createdAt?: string | null
  qrPayload?: QrPayload | null
  qrToken?: string | null
  orderId?: string | null
  displayId?: string | number | null
}

const formatDate = (value?: string | null) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) {
    return new Date().toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

// One label = one die-cut segment. This MUST equal the @page height (and the
// driver's label length) so the box is exactly one page tall - see the .label
// `height` below. Label WIDTH and QR size are set directly in the CSS.
const LABEL_HEIGHT_MM = 70

// Generate the scannable QR (SVG data URL) for an order, matching the server's
// encode options so it drives the same scan-to-advance flow. Returns '' when the
// order has no QR identity (legacy orders) so the label still prints.
const buildOrderQrDataUrl = async (order: PrintLabelOrder): Promise<string> => {
  // Minimal payload (orderId/token only) → low QR version (~v5) → big, thermal-
  // scannable modules. The full identity object made the printed QR ~v13
  // (unscannable). SVG render = vector, no interpolation/no pixel-dropping.
  const text = buildScanText({
    qrPayload: order.qrPayload,
    qrToken: order.qrToken,
    orderId: order.orderId,
  })
  // 5 dots/module → 225px → 28.15mm (vertical label has room). Pixel-exact PNG.
  return svgQrDataUrl(text || '', 5)
}

const buildLabelBlocks = async (orders: PrintLabelOrder[]) => {
  const labels: string[] = []

  // One QR per order.
  const qrDataUrls = await Promise.all(orders.map((order) => buildOrderQrDataUrl(order)))

  orders.forEach((order, orderIndex) => {
    const items = order.items.length > 0 ? order.items : [{ name: 'Item', quantity: 1 }]
    const qrDataUrl = qrDataUrls[orderIndex]
    const qrMarkup = qrDataUrl
      ? `<img class="label-qr" src="${qrDataUrl}" alt="${escapeHtml(order.orderNumber)} QR" />`
      : ''

    const dateText = formatDate(order.createdAt)
    const itemCount = items.reduce((sum, item) => sum + (item.quantity || 1), 0)
    // Full breakdown of everything in the order, e.g. "2×Shirt, 1×T-Shirt, 1×Bedsheet".
    const itemBreakdown = items.map((item) => `${item.quantity || 1}×${item.name}`).join(', ')
    const countLabel = `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`
    const summaryLine = order.displayId
      ? `${escapeHtml(String(order.displayId))} · ${countLabel}`
      : countLabel

    const serviceLine = (order.serviceNames || []).filter(Boolean).join(', ')
    const serviceMarkup = serviceLine
      ? `<div class="label-service">${escapeHtml(serviceLine)}</div>`
      : ''

    // One label per ORDER: service name(s) + order id on top, QR, then the full
    // item description flowing down the tape, then customer + date.
    labels.push(`
      <div class="label">
        ${serviceMarkup}
        <div class="label-order">${escapeHtml(order.orderNumber)}</div>
        ${qrMarkup}
        <div class="label-row label-summary">${summaryLine}</div>
        <div class="label-row label-items">${escapeHtml(itemBreakdown)}</div>
        <div class="label-row"><span class="label-key">Cust:</span> ${escapeHtml(order.customerName)}</div>
        <div class="label-row">${escapeHtml(dateText)}</div>
      </div>
    `)
  })

  return labels.join('')
}

type ToastFn = (message: string, type?: 'success' | 'error' | 'info') => void

export const usePrintLabels = () => {
  const { addToast } = useToast()
  return (orders: PrintLabelOrder[]) => openPrintLabels(orders, addToast)
}

export const openPrintLabels = async (orders: PrintLabelOrder[], addToast?: ToastFn) => {
  const notify = addToast || (() => {})
  if (!orders || orders.length === 0) {
    notify('No orders selected for printing.', 'error')
    return
  }

  // Open the popup synchronously (within the user-gesture call stack) BEFORE the
  // async QR generation, otherwise browsers block it as a non-gesture popup.
  // NOTE: do NOT pass 'noopener'/'noreferrer' here - with those features the
  // browser returns `null` instead of a window handle, so the label HTML could
  // never be written and the button silently did nothing. We open our own
  // generated document (not an untrusted URL), so holding the handle is safe.
  const printWindow = window.open('', '_blank', 'width=900,height=700')
  if (!printWindow) {
    notify('Popup blocked. Please allow popups to print labels.', 'error')
    return
  }

  let labelsMarkup: string
  try {
    labelsMarkup = await buildLabelBlocks(orders)
  } catch {
    notify('Failed to generate label QR codes.', 'error')
    printWindow.close()
    return
  }
  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Rapidry Labels</title>
    <style>
      * { box-sizing: border-box; }
      html, body {
        margin: 0;
        padding: 0;
        /* ROOT-CAUSE FIX (right-shift on the TSC TTP-244): the 104mm (4") head
           lays the page out across its FULL width, not our 50mm @page, and the
           label is loaded LEFT-justified (dots 0-400). A width:100% + centred box
           lands content at ~52mm - off the right edge of a 50mm label. Anchor a
           FIXED media-width box at the page origin instead; content then prints
           at dots 0-400 regardless of the negotiated page width. */
        width: 50mm;
        font-family: "Segoe UI", Arial, sans-serif;
        /* Pure black + heavy weight = crisp, dark output on thermal media. */
        color: #000000;
        background: #FFFFFF;
        -webkit-font-smoothing: none;
        /* Keep bold + bg colours when printing - Safari/Edge otherwise strip
           backgrounds and soften bold weights to grey. */
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      /* One vertical label per tape segment. width:100% fills whatever printable
         width the OS/driver gives; the FLEX stack centres the content both axes
         (regardless of page size or how many lines the item list wraps to), so
         the label is centred identically on macOS and Windows. */
      .label {
        width: 100%;
        /* CRITICAL: height MUST be a fixed mm equal to the @page height, NOT
           100vh. With the driver media set to "Continuous", the page the popup
           negotiates is ambiguously tall, so 100vh balloons past 70mm - Chrome
           then splits one label across multiple sheets ("3 sheets of paper")
           and shrinks to fit ("Scale 60%"). A fixed ${LABEL_HEIGHT_MM}mm box ==
           exactly one die-cut label. (Set the driver media to Labels-With-Gaps
           and the label length to ${LABEL_HEIGHT_MM}mm.) */
        height: ${LABEL_HEIGHT_MM}mm;
        padding: 2mm 2mm;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.6mm;
        text-align: center;
        overflow: hidden;
        page-break-after: always;
        break-after: page;
      }
      .label:last-child {
        page-break-after: auto;
        break-after: auto;
      }
      /* Service name: allow up to TWO lines (long names like "Premium Wash
         & Iron" used to truncate with nowrap+ellipsis), then ellipsize. */
      .label-service {
        font-size: 12px;
        font-weight: 800;
        line-height: 1.15;
        color: #000000;
        max-width: 100%;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-break: break-word;
      }
      .label-order {
        /* 13px + 1mm horizontal padding fits a full 14-char ID
           (e.g. RP-260624-0007) on a 30mm tape without truncating -
           previously 17px clipped real order numbers off the right edge. */
        font-size: 13px;
        font-weight: 800;
        letter-spacing: 0;
        line-height: 1.1;
        color: #000000;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .label-qr {
        /* 225px PNG (5 dots/module, pinned v5) shown at exactly 225÷8 = 28.15mm
           so 1 image px == 1 printer dot. pixelated/crisp-edges = nearest-
           neighbour at a 1:1 ratio = loss-less (the earlier noise was from a
           NON-integer downscale of an SVG; now the px↔mm map is exact). */
        width: 28.15mm;
        height: 28.15mm;
        display: block;
        margin: 0 auto;
        image-rendering: pixelated;
        image-rendering: crisp-edges;
      }
      .label-row {
        font-size: 11px;
        font-weight: 700;
        line-height: 1.25;
        color: #000000;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      /* Order summary line wraps if the displayId is long. */
      .label-summary {
        margin-top: 0.6mm;
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
        word-break: break-word;
      }
      /* The items breakdown is the only line that can grow unbounded with order
         size. Hard-cap it at 6 lines so a 30-item order doesn't push the
         customer + date OFF the tape and shift every other label out of
         alignment. Overflow ellipsizes with "…" - staff still see the count. */
      .label-items {
        font-size: 9px;
        line-height: 1.2;
        word-break: break-word;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
      .label-key {
        color: #000000;
        font-weight: 800;
      }
      /* Page == one die-cut label: 50mm wide × ${LABEL_HEIGHT_MM}mm. The .label
         box height is pinned to this same value (not 100vh) so one label is
         exactly one segment - no multi-sheet split, no fit-to-page shrink.
         NOTE: the physical RIGHT-SHIFT is a printer/driver setting (media origin
         + Continuous-vs-gap media), NOT a CSS issue - set the driver to
         Labels-With-Gaps, run gap calibration, and seat the label guide. */
      @page {
        size: 50mm ${LABEL_HEIGHT_MM}mm;
        margin: 0;
      }
    </style>
  </head>
  <body>
    ${labelsMarkup}
    <script>
      window.onload = () => {
        window.print();
      };
    </script>
  </body>
</html>`

  printWindow.document.write(html)
  printWindow.document.close()
}
